import { createAddress } from "../../../../utils/Helper";

/* Address & Contact — the rules, separated from the markup. */

/* Transcribed verbatim from NewAddressForm.jsx:508. */
export const EMAIL_PATTERN = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

export const isValidEmail = (email) => EMAIL_PATTERN.test(email);

/* Transcribed field by field from handleSubmit's newErrors block, including the exact messages. */
export const validateAddress = (values = {}) => {
  const errors = {};
  const trimmed = (v) => String(v ?? "").trim();

  if (trimmed(values.address_line_1) === "") {
    errors.address_line_1 = "Address Line 1 is required.";
  }
  if (trimmed(values.telephone) === "") {
    errors.telephone = "Phone number is required.";
  }
  if (trimmed(values.city) === "") {
    errors.city = "City is required.";
  }
  if (trimmed(values.state) === "") {
    errors.state = "State is required.";
  }
  if (trimmed(values.country) === "") {
    errors.country = "Country is required.";
  }
  /* Transcribed exactly: the live check is `selectedHours === ""`, so a
     working-hours string of any shape passes. */
  if (values.working_hours === "") {
    errors.working_hours = "Working Hours is required.";
  }
  if (trimmed(values.email) === "") {
    errors.email = "Email Address is required.";
  } else if (!isValidEmail(trimmed(values.email))) {
    errors.email = "Invalid Email Address.";
  }

  return errors;
};

/* Which of the three writes a submit performs. */
export const addressSavePath = ({ verifiedCompany, address }) => {
  const verified =
    verifiedCompany !== null &&
    verifiedCompany !== undefined &&
    typeof verifiedCompany === "object" &&
    Object.keys(verifiedCompany).length !== 0;

  if (verified) {
    return address?.company_address_details_draftID ? "editDraft" : "createDraft";
  }
  if (address) return "update";
  return "create";
};

/* The payload. The two shapes differ, and the difference is transcribed: the DRAFT paths send request_type + current_companyID, and coerce an empty... */
export const addressPayload = (values, { draft = false, linkedId } = {}) => {
  const base = {
    address_line_1: values.address_line_1,
    address_line_2: draft ? values.address_line_2 || null : values.address_line_2,
    postal_code: values.postal_code,
    area: draft ? values.area || null : values.area,
    city: values.city,
    state: values.state,
    country: values.country,
    working_hours: values.working_hours,
    telephone: values.telephone,
    email: values.email,
  };

  if (!draft) return base;

  return {
    /* Ordered as the live object literal is, id first. */
    ...(linkedId !== undefined && linkedId !== null
      ? { company_address_detailsID: linkedId }
      : {}),
    ...base,
    request_type: "company details created",
    current_companyID: values.companyID,
  };
};

/* The card's four rows. The live card builds these with `for (let key in object)` over a whitelist of email / working_hours / telephone, then appends the... */
export const ADDRESS_ROWS = [
  { key: "telephone", label: "Telephone" },
  { key: "email", label: "Email" },
  { key: "working_hours", label: "Working hours" },
  { key: "address", label: "Address" },
];

/* Transcribed: a telephone is stored as "<dial>|<number>" and the live card
   strips the pipe before showing it. `createAddress` is the shared joiner —
   the same one the live card calls. */
export const addressRowValues = (record = {}) => ({
  telephone: String(record.telephone ?? "").replace("|", ""),
  email: record.email ?? "",
  working_hours: String(record.working_hours ?? "").replace("|", ""),
  address: createAddress(record || {}),
});

/* Transcribed from AddressContact.jsx: only rows the company has not deleted
   are shown. `is_deleted` is compared loosely there (== 0), and the API sends
   it as a number today, so both are accepted. */
export const liveAddresses = (company) =>
  (company?.companyAddress || []).filter(
    (row) => Number(row?.company_address_detailsID?.is_deleted) === 0
  );

/* Transcribed from companyStatus(): the list shown when drafts exist is every draft row, PLUS any live address that has no draft of its own — so an address... */
export const mergeDrafts = (draftRows, addresses) => {
  const rows = draftRows || [];
  const draftedIds = rows.map((r) => r?.company_address_detailsID_id);

  const untouched = (addresses || [])
    .map((row) => row?.company_address_detailsID)
    .filter(
      (detail) =>
        Number(detail?.is_deleted) === 0 &&
        !draftedIds.includes(detail?.company_address_detailsID)
    );

  return [...rows, ...untouched];
};

/* Transcribed: the status glyph reads "raised" as awaiting review, the same as
   "Requested". Everything else falls through to SectionStatusPill's own map. */
export const addressStatus = (record) =>
  record?.status === "raised" ? "Requested" : record?.status;
